import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const MAX_VISIBLE = 50;

function matchesQuery(o, q) {
  return String(o.label).toLowerCase().includes(q) || (o.searchText && String(o.searchText).toLowerCase().includes(q));
}

// Keeps option order, always shows pinned rows, caps matching rows at `max`.
function filterOptions(options, q, max) {
  const shown = [];
  let matched = 0;
  let plain = 0;
  for (const o of options) {
    if (o.pinned) { shown.push(o); continue; }
    if (q && !matchesQuery(o, q)) continue;
    matched += 1;
    if (plain < max) { shown.push(o); plain += 1; }
  }
  return { shown, matched, hidden: matched - plain };
}

function OptionList({ options, search, value, loading, maxVisible, testId, onPick }) {
  const q = search.trim().toLowerCase();
  const { shown, matched, hidden } = useMemo(() => filterOptions(options, q, maxVisible), [options, q, maxVisible]);
  const hasPlain = options.some((o) => !o.pinned);
  return (
    <CommandList className="max-h-64">
      {loading && !hasPlain && (
        <p className="py-4 text-center text-sm text-slate-500" data-testid={testId ? `${testId}-loading` : undefined}>Loading…</p>
      )}
      {!loading && q && matched === 0 && (
        <p className="py-4 text-center text-sm text-slate-500" data-testid={testId ? `${testId}-empty` : undefined}>
          No results found
        </p>
      )}
      {shown.map((o) => (
        <CommandItem
          key={o.value}
          value={String(o.value)}
          data-testid={o.testId}
          className={cn("pr-8", o.className)}
          onSelect={() => onPick(o.value)}
        >
          {o.label}
          {o.value === value && <Check className="absolute right-2 h-4 w-4" />}
        </CommandItem>
      ))}
      {hidden > 0 && (
        <p className="py-2 text-center text-xs text-slate-400" data-testid={testId ? `${testId}-more` : undefined}>
          Showing first {maxVisible} of {matched} — type to narrow
        </p>
      )}
    </CommandList>
  );
}

export function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder = "Select",
  disabled = false,
  testId,
  className,
  searchPlaceholder = "Search...",
  loading = false,
  maxVisible = MAX_VISIBLE,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = options.find((o) => o.value === value);
  const triggerLabel = selected ? selected.label : loading && value ? "Loading…" : placeholder;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          data-placeholder={selected ? undefined : ""}
          className={cn(
            "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className="line-clamp-1 text-left">{triggerLabel}</span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[12rem] p-0" data-testid={testId ? `${testId}-popover` : undefined}>
        <Command shouldFilter={false}>
          <CommandInput autoFocus value={search} onValueChange={setSearch} placeholder={searchPlaceholder} data-testid={testId ? `${testId}-search` : undefined} />
          <OptionList
            options={options}
            search={search}
            value={value}
            loading={loading}
            maxVisible={maxVisible}
            testId={testId}
            onPick={(v) => { onValueChange(v); setOpen(false); }}
          />
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function SearchableChecklist({ items, onToggle, searchPlaceholder = "Search...", testId }) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = q ? items.filter((i) => i.searchText.toLowerCase().includes(q)) : items;
  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input className="pl-8 h-9" placeholder={searchPlaceholder} data-testid={testId ? `${testId}-search` : undefined} value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      <div className="rounded-md border border-slate-200 max-h-40 overflow-y-auto p-2 space-y-1" data-testid={testId}>
        {shown.length === 0 && <p className="text-sm text-slate-400 py-2 text-center">No results found</p>}
        {shown.map((i) => (
          <label key={i.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
            <Checkbox data-testid={i.checkboxTestId} checked={i.checked} onCheckedChange={() => onToggle(i.id)} />
            {i.content}
          </label>
        ))}
      </div>
    </div>
  );
}
